import { useEffect, useId, useRef, useState } from 'react';

function TextField({
  label,
  type = 'text',
  value,
  onChange,
  error = '',
  invalid = false,
  autoComplete,
  trailing = null,
  required = true,
  readOnly = false,
}) {
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const [focused, setFocused] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const raised = focused || autofilled || value.length > 0;
  const inputType = trailing === 'password' && passwordVisible ? 'text' : type;
  valueRef.current = value;
  onChangeRef.current = onChange;

  function syncFromDom(node) {
    if (!node || node.value === valueRef.current) return;
    onChangeRef.current(node.value);
  }

  useEffect(() => {
    const node = inputRef.current;
    if (!node || readOnly) return undefined;
    const delays = [0, 50, 150, 400, 800, 1600];
    const timers = delays.map((delay) => window.setTimeout(() => syncFromDom(node), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [readOnly]);

  function isBrowserAutofill(node) {
    try {
      return node.matches(':autofill') || node.matches(':-webkit-autofill');
    } catch {
      return false;
    }
  }

  function handleChange(event) {
    if (!isBrowserAutofill(event.target)) {
      setAutofilled(false);
    }
    onChange(event.target.value);
  }

  function handleAnimationStart(event) {
    if (event.animationName === 'text-field-autofill') {
      setAutofilled(true);
      syncFromDom(event.target);
    }
    if (event.animationName === 'text-field-autofill-off') {
      setAutofilled(false);
      syncFromDom(event.target);
    }
  }

  const className = [
    'text-field',
    raised ? 'is-raised' : '',
    focused && !readOnly ? 'is-focus' : '',
    error || invalid ? 'is-error' : '',
    readOnly ? 'is-readonly' : '',
  ].filter(Boolean).join(' ');

  function handleControlMouseDown(event) {
    if (event.button !== 0) return;
    if (event.target.closest('.text-field__action')) return;
    if (event.target === inputRef.current) return;
    event.preventDefault();
    inputRef.current?.focus();
  }

  return (
    <div className={className}>
      <div className="text-field__control" onMouseDown={handleControlMouseDown}>
        <div className="text-field__body">
          <label className="text-field__caption" htmlFor={inputId}>
            <span>{label}</span>
            {required && !readOnly ? <span className="text-field__mark" aria-hidden="true">*</span> : null}
          </label>
          <input
            ref={inputRef}
            id={inputId}
            className="text-field__input"
            type={inputType}
            value={value}
            autoComplete={autoComplete}
            aria-required={required && !readOnly ? 'true' : 'false'}
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            aria-invalid={error || invalid ? 'true' : 'false'}
            aria-describedby={error ? errorId : undefined}
            spellCheck="false"
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onAnimationStart={handleAnimationStart}
          />
        </div>
        <div className="text-field__actions">
          {trailing === 'clear' && value ? (
            <button
              type="button"
              className="text-field__action"
              aria-label="Очистить"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
            >
              <span className="text-field__icon text-field__icon--clear" />
            </button>
          ) : null}
          {trailing === 'password' && value ? (
            <button
              type="button"
              className="text-field__action"
              aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
              aria-pressed={passwordVisible}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setPasswordVisible((visible) => !visible);
                inputRef.current?.focus();
              }}
            >
              <span
                className={passwordVisible
                  ? 'text-field__icon text-field__icon--eye-open'
                  : 'text-field__icon text-field__icon--eye-closed'}
              />
            </button>
          ) : null}
        </div>
      </div>
      {error ? (
        <p className="text-field__message" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default TextField;
